import React, { useState, useEffect } from 'react';
import DateRangeIcon from '@material-ui/icons/DateRange';
import { useStyles } from './DateTimePickerOne.styles';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import plLocale from 'date-fns/locale/pl';
import { compareDate } from '../../utils/compareDate';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  CalendarPicker,
  CalendarPickerView,
  ClockPicker,
  DatePicker,
  PickersDay,
  PickersDayProps,
  TimePicker,
} from '@mui/x-date-pickers';
import { IconButton, TextField } from '@mui/material';
import { ClockPickerView } from '@mui/x-date-pickers/internals/models/views';

interface DateTimePickerOneProps {
  onChange: (newDate: Date) => void;
  onClose?: () => void;
  selectedDateTime: Date | null | undefined;
  selectedDateTimeFrom?: Date | null | undefined;
  selectedDateTimeTo?: Date | null | undefined;
  minDate?: Date;
  maxDate?: Date;
  dateLabel?: string;
  dateAriaLabel?: string;
  timeLabel?: string;
  timeAriaLabel?: string;
  minDateMessage?: string;
  maxDateMessage?: string;
  automaticDateTimeExitFlow?: boolean;
  singleMode?: boolean;
  isTimeFromError?: boolean;
  isTimeToError?: boolean;
  isTimePicker?: boolean;
}

const MIN_YEAR = 1900;

enum ErrorMessage {
  wrongDate = 'Nieprawidłowa data',
  wrongTime = 'Nieprawidłowa godzina',
}

export const DateTimePickerOne: React.FC<DateTimePickerOneProps> = (props) => {
  const classes = useStyles();

  const [automaticFlowStarted, setAutomaticFlowStarted] = useState<boolean>(false);

  const [editedDate, setEditedDate] = useState<Date | null | undefined>(
    props.selectedDateTime ? new Date(props.selectedDateTime) : null,
  );
  const [editedTime, setEditedTime] = useState<Date | null | undefined>(
    props.selectedDateTime ? new Date(props.selectedDateTime) : null,
  );

  const [viewType, setViewType] = useState<'calendar' | 'clock'>('calendar');
  const [calendarViewType, setCalendarViewType] = useState<CalendarPickerView>(editedDate === null ? 'year' : 'day');
  const [clockViewType, setClockViewType] = useState<ClockPickerView>('hours');

  const [isDateValidationError, setIsDateValidationError] = useState<boolean>(false);
  const [isTimeValidationError, setIsTimeValidationError] = useState<boolean>(false);

  useEffect(() => {
    if (!editedDate || !editedTime || isDateValidationError || isTimeValidationError) {
      return;
    }
    try {
      if (
        editedDate &&
        editedTime &&
        props.selectedDateTime &&
        Date.parse(editedDate.toISOString()) &&
        Date.parse(editedTime.toISOString())
      ) {
        const dateTime: Date = new Date(editedDate);
        dateTime.setHours(editedTime.getHours());
        dateTime.setMinutes(editedTime.getMinutes());
        props.onChange(dateTime);
        if (props.automaticDateTimeExitFlow && automaticFlowStarted) {
          if (viewType === 'clock' && clockViewType === 'minutes') {
            props.onClose && props.onClose();
          }
        }
      }
    } catch {
      //do nothing in case of error. Please don't refactor
    }
  }, [editedDate, editedTime]);

  const handleDateChange = (date: Date | null) => {
    if (date && date.getFullYear() >= MIN_YEAR && props.maxDate && date.getTime() <= props.maxDate.getTime()) {
      setEditedDate(date);
      setIsDateValidationError(false);
      if (props.automaticDateTimeExitFlow) {
        setAutomaticFlowStarted(true);
        setIsDateValidationError(false);
      }
    } else {
      setIsDateValidationError(true);
    }
  };

  const handleTimeChange = (date: Date | null) => {
    if (clockViewType === 'hours') setClockViewType('minutes');
    setEditedTime(date);
  };

  const handleTimeChangeFromKeyboard = (date: Date | null, keyboardInputValue?: string) => {
    if (keyboardInputValue && keyboardInputValue.match(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/) && date) {
      setEditedTime(date);
      setIsTimeValidationError(false);
    } else {
      setIsTimeValidationError(true);
    }
  };

  const getDayElementForCalendar = (
    day: Date | undefined,
    selectedDays: (Date | undefined)[],
    pickersDayProps: PickersDayProps<Date | undefined>,
  ): JSX.Element => {
    const divClasses = [];
    if (day && !props.singleMode) {
      if (props.maxDate && editedDate) {
        if (day <= props.maxDate && day >= editedDate) {
          divClasses.push('dayInSelectedRange');
        }
        if (compareDate(day, props.maxDate)) {
          divClasses.push('lastDayFromRange');
        }
        if (compareDate(day, editedDate)) {
          divClasses.push('firstDayFromRange');
        }
      }
      if (props.minDate && editedDate) {
        if (day >= props.minDate && day <= editedDate) {
          divClasses.push('dayInSelectedRange');
        }
        if (compareDate(day, props.minDate)) {
          divClasses.push('firstDayFromRange');
        }
        if (compareDate(day, editedDate)) {
          divClasses.push('lastDayFromRange');
        }
      }
    }
    return (
      <div className={divClasses.join(' ')}>
        <PickersDay {...pickersDayProps} />
      </div>
    );
  };

  const isTimeRangeError = () => {
    return (
      (props.timeLabel === 'Godz. zakończenia' && props.isTimeToError) ||
      (props.timeLabel === 'Godz. rozpoczęcia' && props.isTimeFromError)
    );
  };

  return (
    <div className={classes.dateTimePicker}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={plLocale}>
        <DatePicker
          value={editedDate}
          inputFormat="dd/MM/yyyy"
          onChange={handleDateChange}
          disableOpenPicker
          label={props.dateLabel ?? 'Data rozpoczęcia'}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="dense"
              size="small"
              color="primary"
              InputProps={{
                endAdornment: (
                  <IconButton
                    disableRipple
                    onClick={() => setCalendarViewType('year')}
                    style={{ marginRight: '-14px' }}
                  >
                    <DateRangeIcon />
                  </IconButton>
                ),
              }}
              error={isDateValidationError}
              helperText={isDateValidationError && ErrorMessage.wrongDate}
              onClick={() => setViewType('calendar')}
            />
          )}
        />
        {(props.isTimePicker === undefined || props.isTimePicker === true) && (
          <TimePicker
            label={props.timeLabel ?? 'Godz. rozpoczęcia'}
            mask="__:__"
            disableOpenPicker
            value={editedTime}
            onChange={handleTimeChangeFromKeyboard}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                size="small"
                color="primary"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      disableRipple
                      onClick={() => setClockViewType('hours')}
                      style={{ marginRight: '-14px' }}
                    >
                      <AccessTimeIcon />
                    </IconButton>
                  ),
                }}
                error={isTimeValidationError || isTimeRangeError()}
                helperText={(isTimeValidationError || isTimeRangeError()) && ErrorMessage.wrongTime}
                onClick={() => setViewType('clock')}
              />
            )}
          />
        )}

        {viewType === 'calendar' ? (
          <CalendarPicker
            date={editedDate}
            onChange={setEditedDate}
            view={calendarViewType}
            maxDate={props.maxDate}
            minDate={props.minDate}
            showDaysOutsideCurrentMonth
            disableHighlightToday
            renderDay={getDayElementForCalendar}
            onYearChange={() => {
              setCalendarViewType('month');
            }}
            onMonthChange={() => {
              setCalendarViewType('day');
            }}
            componentsProps={{
              switchViewButton: {
                onClick: () => {
                  switch (calendarViewType) {
                    case 'day':
                      setCalendarViewType('year');
                      break;
                    case 'month':
                      setCalendarViewType('day');
                      break;
                    case 'year':
                      setCalendarViewType('month');
                      break;
                  }
                },
              },
            }}
          />
        ) : (
          <ClockPicker date={editedTime!} onChange={handleTimeChange} view={clockViewType} minutesStep={5} />
        )}
      </LocalizationProvider>
    </div>
  );
};
